import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { EntShell, BtnLink, Btn, Kicker, StatusBadge, useEntMe } from "@/components/enterprise/Ent";

const OrgEvents = ({ org }) => {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/enterprise/orgs/${org.org_id}/events`, { withCredentials: true }).then((r) => setEvents(r.data.events)).catch(() => setEvents([]));
  }, [org.org_id]);
  return (
    <section className="bg-white border border-[#ddd5c9] rounded-lg" data-testid={`dash-org-${org.org_id}`} aria-labelledby={`h-${org.org_id}`}>
      <div className="px-6 py-5 border-b border-[#efe9e0] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 id={`h-${org.org_id}`} className="font-display text-2xl">{org.name}</h2>
          {org.department && <div className="text-sm text-[#5a524b]">{org.department}</div>}
        </div>
        <span className="text-xs px-2 py-1 rounded bg-[#f1ece4]">Organization administrator</span>
      </div>
      {events === null ? <p className="px-6 py-5 text-sm">Loading events…</p> : events.length === 0 ? (
        <p className="px-6 py-5 text-sm text-[#5a524b]">No events yet.</p>
      ) : (
        <ul>
          {events.map((e) => (
            <li key={e.event_id} className="border-b border-[#efe9e0] last:border-0">
              <Link to={`/enterprise/events/${e.event_id}`} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-[#faf8f4]" data-testid={`dash-event-${e.event_id}`}>
                <div>
                  <div className="font-semibold">{e.program.name}</div>
                  <div className="text-sm text-[#5a524b]">{e.capacity?.people} · {e.option === "ready" ? "Ready-to-Play" : "Customized"}{e.requested_date ? ` · ${e.requested_date}` : ""} · Code {e.event_code}</div>
                </div>
                <StatusBadge status={e.status} testid={`dash-event-status-${e.event_id}`} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default function EnterpriseDashboard() {
  const { me } = useEntMe();
  const { logout } = useAuth();
  useEffect(() => { document.title = "Dashboard | WasntMe.Party Enterprise"; }, []);
  if (!me) return <EntShell><div className="max-w-5xl mx-auto px-6 py-20" aria-busy="true">Loading…</div></EntShell>;
  return (
    <EntShell>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Kicker>Enterprise dashboard</Kicker>
            <h1 className="font-display text-4xl mt-3" data-testid="dash-title">Welcome, {me.user.name || me.user.email}.</h1>
          </div>
          <div className="flex gap-3">
            <BtnLink to="/enterprise/setup" data-testid="dash-new-event">Book a program</BtnLink>
            <Btn variant="outline" onClick={async () => { await logout(); window.location.href = "/enterprise"; }} data-testid="dash-logout">Sign out</Btn>
          </div>
        </div>
        <div className="mt-10 space-y-6">
          {me.orgs.length === 0 ? (
            <div className="bg-white border border-[#ddd5c9] rounded-lg p-8" data-testid="dash-empty">
              <h2 className="font-display text-2xl">No organization yet</h2>
              <p className="mt-2 text-[15px] text-[#3b3530]">Book your first program to set up your organization.</p>
              <BtnLink to="/enterprise/setup" className="mt-5">Book a program</BtnLink>
            </div>
          ) : me.orgs.map((o) => <OrgEvents key={o.org_id} org={o} />)}
        </div>
      </div>
    </EntShell>
  );
}
